/**
 * Lost-item reporting queue — `/api/bi/lost-items/*`.
 *
 * Spine is `order.LostItem`: a packer reports units missing at pick time, an
 * approver accepts or rejects, and approval is what writes the loss off stock.
 * Rejecting restores stock, so the two actions are true inverses.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { env } from '@/lib/env';

export type LostItemStatus = 'pending' | 'approved' | 'rejected';
export type LostItemReason = 'not_available' | 'partial_missing' | 'other';

export interface LostItemUserRef {
  id: number;
  name: string;
}

export interface LostItem {
  id: number;
  product_id: number | null;
  availability_id: number | null;
  product_name: string | null;
  product_image: string | null;
  store: { id: number; name: string } | null;
  order_id: number | null;
  order_item_id: number | null;
  quantity: number;
  found_quantity: number;
  /** Reported minus found — the quantity that actually gets written off. */
  net_units: number;
  reason: LostItemReason;
  notes: string;
  approval_status: LostItemStatus;
  reported_by: LostItemUserRef | null;
  reported_at: string | null;
  approved_by: LostItemUserRef | null;
  approved_at: string | null;
  applied_to_stock: boolean;
  applied_at: string | null;
  image: string | null;
  unit_cost: number | null;
  estimated_value: number | null;
}

export interface LostItemTag {
  id: number;
  tag: string;
  product_unit_id: number | null;
  unit_status: string | null;
}

export interface LostItemDetail extends LostItem {
  tags: LostItemTag[];
  order: {
    id: number;
    status: string;
    payment_type: string;
    final_total: number;
    created_at: string | null;
    order_item: { id: number; quantity: number; price: number } | null;
  } | null;
  ledger_entry: {
    id: number;
    ts: string | null;
    qty_out: number;
    balance: number;
    unit_price: number;
    reference: string;
  } | null;
}

export interface PaginatedLostItems {
  results: LostItem[];
  totals: { units: number; net_units: number; pending: number };
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

export interface LostItemsOverview {
  range: { start: string; end: string };
  kpis: {
    reports: number;
    units: number;
    net_units: number;
    approved: number;
    rejected: number;
    pending_in_range: number;
    approval_rate: number;
    reporters: number;
    skus: number;
    median_approval_hours: number | null;
    pending_total: number;
    pending_net_units: number;
  };
  pending_aging: { '0_1d': number; '1_3d': number; '3_7d': number; '7d_plus': number };
  hygiene: {
    approved_not_applied: number;
    rejected_still_applied: number;
    pending_already_applied: number;
  };
  by_reason: Array<{ reason: string; reports: number; units: number; net_units: number }>;
  by_store: Array<{
    store_id: number | null;
    store_name: string;
    reports: number;
    net_units: number;
    pending: number;
  }>;
  by_day: Array<{ day: string | null; reports: number; net_units: number }>;
  top_skus: Array<{
    product_id: number | null;
    product_name: string | null;
    reports: number;
    net_units: number;
    estimated_value: number | null;
  }>;
}

export interface LostItemsAttribution {
  range: { start: string; end: string };
  caveat: string;
  reporters: Array<{
    user_id: number | null;
    user_name: string;
    reports: number;
    net_units: number;
    approved: number;
    rejected: number;
    pending: number;
    skus: number;
    rejection_rate: number;
  }>;
  approvers: Array<{
    user_id: number | null;
    user_name: string;
    decisions: number;
    approved: number;
    rejected: number;
    net_units: number;
    approval_rate: number;
  }>;
}

export interface LostItemFilters {
  approvalStatus?: LostItemStatus | 'decided' | 'all' | null;
  reason?: LostItemReason | 'all' | null;
  productId?: number | null;
  orderId?: number | null;
  reportedBy?: number | null;
  approvedBy?: number | null;
  appliedToStock?: boolean | null;
  q?: string | null;
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
  page?: number;
  pageSize?: number;
}

function listParams(f: LostItemFilters) {
  return {
    page: f.page ?? 1,
    page_size: f.pageSize ?? 25,
    approval_status:
      f.approvalStatus && f.approvalStatus !== 'all' ? f.approvalStatus : undefined,
    reason: f.reason && f.reason !== 'all' ? f.reason : undefined,
    product_id: f.productId ?? undefined,
    order_id: f.orderId ?? undefined,
    reported_by: f.reportedBy ?? undefined,
    approved_by: f.approvedBy ?? undefined,
    applied_to_stock: f.appliedToStock ?? undefined,
    q: f.q ?? undefined,
    start: f.start ?? undefined,
    end: f.end ?? undefined,
    store_id: f.storeId ?? undefined,
  };
}

export function useLostItems(f: LostItemFilters) {
  return useQuery({
    queryKey: [
      'bi',
      'lost-items',
      'list',
      f.approvalStatus ?? null,
      f.reason ?? null,
      f.productId ?? null,
      f.orderId ?? null,
      f.reportedBy ?? null,
      f.approvedBy ?? null,
      f.appliedToStock ?? null,
      f.q ?? null,
      f.start ?? null,
      f.end ?? null,
      f.storeId ?? null,
      f.page ?? 1,
      f.pageSize ?? 25,
    ],
    queryFn: () => get<PaginatedLostItems>('/api/bi/lost-items/', { params: listParams(f) }),
    placeholderData: keepPreviousData,
  });
}

export function useLostItemDetail(id: number | null) {
  return useQuery({
    queryKey: ['bi', 'lost-items', 'detail', id],
    queryFn: () => get<LostItemDetail>(`/api/bi/lost-items/${id}/`),
    enabled: id != null,
  });
}

export function useLostItemsOverview(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: ['bi', 'lost-items', 'overview', f.start ?? null, f.end ?? null, f.storeId ?? null],
    queryFn: () =>
      get<LostItemsOverview>('/api/bi/lost-items/overview/', {
        params: {
          start: f.start ?? undefined,
          end: f.end ?? undefined,
          store_id: f.storeId ?? undefined,
        },
      }),
  });
}

export function useLostItemsAttribution(f: {
  start?: string | null;
  end?: string | null;
  storeId?: number | null;
}) {
  return useQuery({
    queryKey: [
      'bi',
      'lost-items',
      'attribution',
      f.start ?? null,
      f.end ?? null,
      f.storeId ?? null,
    ],
    queryFn: () =>
      get<LostItemsAttribution>('/api/bi/lost-items/attribution/', {
        params: {
          start: f.start ?? undefined,
          end: f.end ?? undefined,
          store_id: f.storeId ?? undefined,
        },
      }),
  });
}

/**
 * Approve or reject. Both invalidate the whole `lost-items` tree because a
 * decision moves the row between status tabs and shifts every KPI on the page.
 */
export function useLostItemDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'approve' | 'reject' }) =>
      post<LostItem & { changed: boolean; detail?: string }>(
        `/api/bi/lost-items/${id}/${action}/`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bi', 'lost-items'] });
      // Approval writes the loss off stock, so shrinkage and the ledger move too.
      void qc.invalidateQueries({ queryKey: ['bi', 'shrinkage'] });
    },
  });
}

/** Build a media URL for a lost-item evidence photo or catalogue thumbnail. */
export function lostItemImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  const cleaned = path.replace(/^\/+/, '');
  // Paths are stored relative to MEDIA_ROOT, e.g. `src/images/lost_items/...`.
  return `${env.apiBase}/media/${cleaned}`;
}

export const LOST_ITEM_REASON_LABEL: Record<string, string> = {
  not_available: 'Not Available',
  partial_missing: 'Partial Missing',
  other: 'Other',
};

export const LOST_ITEM_STATUS_TONE: Record<LostItemStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
};
